// Shared markup model for the Drawings viewer — mirrors the Utility Guard
// photo/plan annotation shapes. All coordinates are 0..1 fractions of the page,
// so markup renders identically at any zoom or screen size (converted to pixels
// by the overlay/canvas which know the rendered page size).

export type Point = [number, number];

export type EditorTool =
  | "select"
  | "arrow"
  | "line"
  | "rect"
  | "box"
  | "circle"
  | "draw"
  | "text"
  | "link"
  | "symbol";

// `rotation` (degrees, around the shape's centre) and — for point-anchored
// text/symbols — `scale` are optional transforms applied by the select tool's
// resize/rotate handles. Absent = 0° / 1x.
export type Shape =
  | { id: string; tool: "arrow" | "line"; color: string; from: Point; to: Point; rotation?: number }
  | {
      id: string;
      tool: "rect" | "box";
      color: string;
      topLeft: Point;
      bottomRight: Point;
      rotation?: number;
    }
  | { id: string; tool: "circle"; color: string; center: Point; radius: Point; rotation?: number }
  | { id: string; tool: "freehand"; color: string; points: Point[]; rotation?: number }
  | {
      id: string;
      tool: "text";
      color: string;
      position: Point;
      text: string;
      rotation?: number;
      scale?: number;
    }
  | {
      id: string;
      tool: "link";
      color: string;
      topLeft: Point;
      bottomRight: Point;
      href: string;
      rotation?: number;
    }
  | {
      id: string;
      tool: "symbol";
      color: string;
      position: Point;
      key: string;
      rotation?: number;
      scale?: number;
    };

// The palette in the markup toolbar (matches the Utility Guard swatches).
export const MARKUP_COLORS = [
  "#DC2626", // red
  "#EA580C", // orange
  "#EAB308", // amber
  "#16A34A", // green
  "#2563EB", // blue
  "#7C3AED", // violet
  "#FFFFFF", // white
  "#111827", // near-black
];

export interface SymbolDef {
  key: string;
  label: string;
  category: string;
  // 24x24 stroked path(s), lucide-style — rendered centred on the point.
  path: string;
}

export const SYMBOL_CATEGORIES = [
  "electrical",
  "plumbing",
  "mechanical",
  "security",
  "safety",
] as const;

// A compact catalogue (Phase 1). Paths are simplified 24x24 icons; the overlay
// draws them stroked in the shape's colour on a white disc.
export const SYMBOLS: SymbolDef[] = [
  { key: "power", label: "Power", category: "electrical", path: "M13 2 3 14h7l-1 8 10-12h-7l1-8Z" },
  { key: "outlet", label: "Outlet", category: "electrical", path: "M4 4h16v16H4zM9 9v2M15 9v2M9 15h6" },
  { key: "light", label: "Light", category: "electrical", path: "M9 18h6M10 22h4M12 2a7 7 0 0 0-4 12c1 1 1 2 1 3h6c0-1 0-2 1-3a7 7 0 0 0-4-12Z" },
  { key: "water", label: "Water point", category: "plumbing", path: "M12 2s6 7 6 12a6 6 0 0 1-12 0c0-5 6-12 6-12Z" },
  { key: "valve", label: "Valve", category: "plumbing", path: "M12 3v6M8 12h8M6 20l6-6 6 6M9 20h6" },
  { key: "fan", label: "Fan", category: "mechanical", path: "M12 12v.01M12 12c0-4 6-4 6 0s-6 4-6 0M12 12c4 0 4 6 0 6s-4-6 0-6" },
  { key: "heat", label: "Heating", category: "mechanical", path: "M8 2s2 2 2 5-2 3-2 5 2 4 2 4M14 4s2 2 2 5-2 3-2 5 2 4 2 4" },
  { key: "cctv", label: "CCTV", category: "security", path: "M3 7l14-3 1 4-14 3zM4 8l1 4M6 14v6h4v-4" },
  { key: "lock", label: "Locked access", category: "security", path: "M6 10h12v10H6zM9 10V7a3 3 0 0 1 6 0v3" },
  { key: "hazard", label: "Hazard", category: "safety", path: "M12 3 2 20h20L12 3ZM12 9v5M12 17v.01" },
  { key: "exit", label: "Exit", category: "safety", path: "M9 4H5v16h4M14 8l4 4-4 4M18 12H9" },
  { key: "ppe", label: "PPE area", category: "safety", path: "M4 15a8 8 0 0 1 16 0M4 15h16v3H4zM8 15V9a4 4 0 0 1 8 0v6" },
];

export function findSymbol(key: string): SymbolDef | undefined {
  return SYMBOLS.find((s) => s.key === key);
}

// SVG points string for a freehand/polyline in pixel space.
export function polylinePx(points: Point[], w: number, h: number): string {
  return points.map((p) => `${p[0] * w},${p[1] * h}`).join(" ");
}

// ---- transform geometry (shared by the overlay + the editor's handles) ----

const clamp01 = (n: number): number => Math.min(1, Math.max(0, n));

export function rotationOf(s: Shape): number {
  return (s as { rotation?: number }).rotation ?? 0;
}
export function scaleOf(s: Shape): number {
  return (s as { scale?: number }).scale ?? 1;
}

/** Axis-aligned (unrotated) 0..1 bounds of a shape: [x1, y1, x2, y2]. */
export function boundsFrac(s: Shape): [number, number, number, number] {
  if (s.tool === "arrow" || s.tool === "line") {
    return [Math.min(s.from[0], s.to[0]), Math.min(s.from[1], s.to[1]), Math.max(s.from[0], s.to[0]), Math.max(s.from[1], s.to[1])];
  }
  if (s.tool === "rect" || s.tool === "box" || s.tool === "link") {
    return [Math.min(s.topLeft[0], s.bottomRight[0]), Math.min(s.topLeft[1], s.bottomRight[1]), Math.max(s.topLeft[0], s.bottomRight[0]), Math.max(s.topLeft[1], s.bottomRight[1])];
  }
  if (s.tool === "circle") {
    return [s.center[0] - s.radius[0], s.center[1] - s.radius[1], s.center[0] + s.radius[0], s.center[1] + s.radius[1]];
  }
  if (s.tool === "freehand") {
    const xs = s.points.map((p) => p[0]);
    const ys = s.points.map((p) => p[1]);
    return [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
  }
  // point-anchored (text / symbol) — a small box that grows with scale
  const half = 0.028 * scaleOf(s);
  const [px, py] = s.tool === "text" || s.tool === "symbol" ? s.position : [0, 0];
  return [px - half, py - half, px + half, py + half];
}

export function centerFrac(s: Shape): Point {
  const [x1, y1, x2, y2] = boundsFrac(s);
  return [(x1 + x2) / 2, (y1 + y2) / 2];
}

/** Rotate a pixel point around a pixel centre by `deg` (matches SVG rotate). */
export function rotatePx(
  p: [number, number],
  c: [number, number],
  deg: number,
): [number, number] {
  const rad = (deg * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);
  const dx = p[0] - c[0];
  const dy = p[1] - c[1];
  return [c[0] + dx * cos - dy * sin, c[1] + dx * sin + dy * cos];
}

/** Scale a shape's geometry around centre `c` by (sx, sy) in 0..1 space.
 *  Point-anchored shapes scale their `scale` field instead of coordinates. */
export function scaleShape(s: Shape, c: Point, sx: number, sy: number): Shape {
  const sc = (p: Point): Point => [
    clamp01(c[0] + (p[0] - c[0]) * sx),
    clamp01(c[1] + (p[1] - c[1]) * sy),
  ];
  switch (s.tool) {
    case "arrow":
    case "line":
      return { ...s, from: sc(s.from), to: sc(s.to) };
    case "rect":
    case "box":
    case "link":
      return { ...s, topLeft: sc(s.topLeft), bottomRight: sc(s.bottomRight) };
    case "circle":
      return { ...s, center: sc(s.center), radius: [Math.abs(s.radius[0] * sx), Math.abs(s.radius[1] * sy)] };
    case "freehand":
      return { ...s, points: s.points.map(sc) };
    case "text":
    case "symbol":
      return { ...s, scale: Math.min(6, Math.max(0.25, scaleOf(s) * (Math.abs(sx) + Math.abs(sy)) / 2)) };
  }
}
